// BotTrainer.jsx
//
// Drop-in panel that lets agents upload call recordings and scripts to train
// the AI bots / roleplay engine. Backed by /api/training (already implemented).
//
// Usage:
//   <BotTrainer />                       — defaults
//   <BotTrainer compact />               — single-column layout for narrow panels
//   <BotTrainer onMaterialAdded={fn} />  — callback after a successful upload

import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiGet, apiPost } from '../lib/api.js';
import { planCompression, compressAudioFile, formatCompressionSummary } from '../lib/compressAudio.js';
import {
  Upload, Mic, FileText, Loader2, CheckCircle2, Trash2, Pin, Star,
  Headphones, BookOpen, Sparkles, Plus,
} from 'lucide-react';

const LEAD_TYPES = ['Universal', 'FEX', 'Mortgage', 'IUL', 'Term', 'Medicare', 'ACA'];
const MATERIAL_TYPES = [
  { v: 'script', label: 'Script', icon: FileText },
  { v: 'transcript', label: 'Call Transcript', icon: Headphones },
  { v: 'philosophy', label: 'Philosophy', icon: Sparkles },
  { v: 'objection', label: 'Objection Handling', icon: Star },
  { v: 'note', label: 'Note', icon: BookOpen },
];

export default function BotTrainer({ compact = false, onMaterialAdded }) {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({
    queryKey: ['training-materials'],
    queryFn: () => apiGet('/training'),
  });
  const items = data?.items || [];

  return (
    <div className={`grid ${compact ? 'grid-cols-1' : 'lg:grid-cols-2'} gap-4`}>
      <UploadCallCard onUploaded={() => { refetch(); onMaterialAdded?.(); }} />
      <UploadScriptCard onUploaded={() => { refetch(); onMaterialAdded?.(); }} />
      <div className={compact ? '' : 'lg:col-span-2'}>
        <MaterialsList items={items} onChange={() => qc.invalidateQueries({ queryKey: ['training-materials'] })} />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Upload a call recording (audio/video → Whisper → transcript material)
// ────────────────────────────────────────────────────────────────────────
function UploadCallCard({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [leadType, setLeadType] = useState('Universal');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);
  const inputRef = useRef(null);

  function pickFile(f) {
    if (!f) { setFile(null); return; }
    const plan = planCompression(f);
    if (plan.action === 'reject') {
      toast.error(plan.reason, { duration: 8000 });
      if (inputRef.current) inputRef.current.value = '';
      setFile(null);
      return;
    }
    setFile(f);
  }

  async function submit(e) {
    e?.preventDefault();
    if (!file) { toast.error('Pick a recording first'); return; }
    setBusy(true);
    let fileToUpload = file;
    const plan = planCompression(file);
    if (plan.action === 'compress') {
      setCompressing(true);
      setCompressProgress(0);
      try {
        const result = await compressAudioFile(file, {
          onProgress: (p) => setCompressProgress(p),
        });
        fileToUpload = result.file;
        toast.success(formatCompressionSummary(result.originalSize, result.compressedSize), { duration: 5000 });
      } catch (compressErr) {
        // eslint-disable-next-line no-console
        console.warn('[compress] failed, uploading original:', compressErr);
      } finally {
        setCompressing(false);
        setCompressProgress(0);
      }
    }
    const fd = new FormData();
    fd.append('file', fileToUpload);
    if (title) fd.append('title', title);
    fd.append('leadType', leadType);
    if (tags) fd.append('tags', tags);
    try {
      const { data } = await api.post('/training/upload-recording', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 5 * 60 * 1000,
      });
      if (data?.transcribed) toast.success('Call transcribed and saved');
      else toast.success('Call saved — transcript pending');
      setFile(null); setTitle(''); setTags('');
      if (inputRef.current) inputRef.current.value = '';
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="panel p-5 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg civix-gradient flex items-center justify-center shadow-glow-purple">
          <Mic className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="font-semibold">Upload a Call</div>
          <div className="text-xs text-white/50">Audio or video — auto-transcribed by AI</div>
        </div>
      </div>

      <label className="block">
        <div className="label">Recording (mp3, m4a, wav, mp4, mov)</div>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*,video/*,.mp3,.m4a,.wav,.mp4,.mov,.webm"
          onChange={(e) => pickFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-white/70 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-civix-purple/20 file:text-white file:cursor-pointer hover:file:bg-civix-purple/30"
        />
        {file && (
          <div className="text-xs text-white/50 mt-1">
            {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
          </div>
        )}
      </label>

      <div className="grid grid-cols-2 gap-2">
        <input className="input" placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <select className="input" value={leadType} onChange={(e) => setLeadType(e.target.value)}>
          {LEAD_TYPES.map((lt) => <option key={lt} value={lt}>{lt}</option>)}
        </select>
      </div>
      <input className="input" placeholder="Tags (comma-separated)" value={tags} onChange={(e) => setTags(e.target.value)} />

      <button type="submit" disabled={!file || busy} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
        {compressing
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Compressing recording… {Math.round((compressProgress || 0) * 100)}%</>
          : busy
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Transcribing…</>
            : <><Upload className="w-4 h-4" /> Train on this call</>}
      </button>
      <div className="text-[11px] text-white/40">
        Uploads up to 80 MB. Transcripts feed AI Bots, Roleplay personas, and the on-call coach.
      </div>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Upload a script (txt, md, docx) OR paste raw text
// ────────────────────────────────────────────────────────────────────────
function UploadScriptCard({ onUploaded }) {
  const [mode, setMode] = useState('paste'); // 'paste' | 'file'
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState('script');
  const [leadType, setLeadType] = useState('Universal');
  const [tags, setTags] = useState('');
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  async function submit(e) {
    e?.preventDefault();
    if (mode === 'file' && !file) { toast.error('Pick a file first'); return; }
    if (mode === 'paste' && !body.trim()) { toast.error('Paste some text first'); return; }
    if (mode === 'paste' && !title.trim()) { toast.error('Add a title'); return; }

    setBusy(true);
    try {
      if (mode === 'file') {
        const fd = new FormData();
        fd.append('file', file);
        if (title) fd.append('title', title);
        fd.append('type', type);
        fd.append('leadType', leadType);
        if (tags) fd.append('tags', tags);
        fd.append('pinned', pinned ? 'true' : 'false');
        await api.post('/training/upload-script', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await apiPost('/training', { title, type, leadType, tags, body, pinned });
      }
      toast.success('Material saved — bots will use it on the next session');
      setFile(null); setBody(''); setTitle(''); setTags(''); setPinned(false);
      if (inputRef.current) inputRef.current.value = '';
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="panel p-5 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg civix-gradient flex items-center justify-center shadow-glow-purple">
          <FileText className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="font-semibold">Upload a Script</div>
          <div className="text-xs text-white/50">Pitch, rebuttals, philosophy — paste text or upload a file</div>
        </div>
      </div>

      <div className="flex gap-1 panel p-1">
        <button type="button" onClick={() => setMode('paste')} className={`flex-1 py-1.5 rounded-sm text-xs ${mode === 'paste' ? 'bg-civix-purple/20 text-white' : 'text-white/50'}`}>Paste text</button>
        <button type="button" onClick={() => setMode('file')} className={`flex-1 py-1.5 rounded-sm text-xs ${mode === 'file' ? 'bg-civix-purple/20 text-white' : 'text-white/50'}`}>Upload .txt / .md / .docx</button>
      </div>

      <input className="input" placeholder="Title — e.g. FEX Opening Pitch" value={title} onChange={(e) => setTitle(e.target.value)} />

      {mode === 'paste' ? (
        <textarea
          className="input min-h-[140px] font-mono text-sm"
          placeholder="Paste the full script, transcript, or notes…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      ) : (
        <input
          ref={inputRef}
          type="file"
          accept=".txt,.md,.docx,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-white/70 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-civix-purple/20 file:text-white file:cursor-pointer hover:file:bg-civix-purple/30"
        />
      )}

      <div className="grid grid-cols-2 gap-2">
        <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
          {MATERIAL_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
        <select className="input" value={leadType} onChange={(e) => setLeadType(e.target.value)}>
          {LEAD_TYPES.map((lt) => <option key={lt} value={lt}>{lt}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-3">
        <input className="input flex-1" placeholder="Tags (comma-separated)" value={tags} onChange={(e) => setTags(e.target.value)} />
        <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
          Pin
        </label>
      </div>

      <button type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Plus className="w-4 h-4" /> Add to bot training</>}
      </button>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Existing materials list — quick view + delete
// ────────────────────────────────────────────────────────────────────────
function MaterialsList({ items, onChange }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? items : items.filter((m) => m.type === filter);

  async function remove(m) {
    if (!confirm(`Delete "${m.title}"?`)) return;
    try {
      await api.delete(`/training/${m._id}${m.seed ? '?force=true' : ''}`);
      toast.success('Deleted');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  }

  return (
    <div className="panel p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-semibold flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-civix-purple" />
          Training Library
          <span className="text-xs text-white/50 font-normal">{items.length} item{items.length === 1 ? '' : 's'}</span>
        </div>
        <div className="flex gap-1 panel p-1 text-xs">
          <button onClick={() => setFilter('all')} className={`px-2 py-1 rounded-sm ${filter === 'all' ? 'bg-civix-purple/20 text-white' : 'text-white/50'}`}>All</button>
          {MATERIAL_TYPES.map((t) => (
            <button key={t.v} onClick={() => setFilter(t.v)} className={`px-2 py-1 rounded-sm ${filter === t.v ? 'bg-civix-purple/20 text-white' : 'text-white/50'}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center text-sm text-white/40 py-8">
          No materials yet. Upload a call or paste a script above to start training your bots.
        </div>
      ) : (
        <div className="divide-y divide-civix-border/40">
          {filtered.slice(0, 50).map((m) => {
            const TypeIcon = (MATERIAL_TYPES.find((t) => t.v === m.type) || MATERIAL_TYPES[0]).icon;
            return (
              <div key={m._id} className="flex items-center gap-3 py-2.5 text-sm">
                <TypeIcon className="w-4 h-4 text-civix-purple/70 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{m.title}</span>
                    {m.pinned && <Pin className="w-3 h-3 text-amber-400" />}
                    {m.seed && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-white/5 text-white/40">seed</span>}
                    {m.source?.origin === 'transcription' && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-civix-purple/20 text-civix-purple">transcribed</span>}
                  </div>
                  <div className="text-[11px] text-white/40 flex gap-2">
                    <span>{m.leadType}</span>
                    <span>·</span>
                    <span>{m.type}</span>
                    {m.source?.filename && <><span>·</span><span className="truncate">{m.source.filename}</span></>}
                  </div>
                </div>
                {m.enabled !== false && <CheckCircle2 className="w-4 h-4 text-emerald-400/70 shrink-0" />}
                <button onClick={() => remove(m)} className="text-white/40 hover:text-red-400 transition" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          {filtered.length > 50 && (
            <div className="text-xs text-white/40 pt-3">Showing 50 of {filtered.length}.</div>
          )}
        </div>
      )}
    </div>
  );
}
